// client/src/pages/AddressesPage.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Button, Stack, Card, CardContent, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const emptyForm = { label: 'Home', name: '', addressLine1: '', addressLine2: '', city: '', province: '', postalCode: '', country: 'South Africa', phone: '', isDefault: false };

const AddressesPage = () => {
  const { token } = useContext(AuthContext);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchAddresses = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/addresses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setAddresses(await response.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const openCreate = () => { setEditing(null); setFormData(emptyForm); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setFormData({
      label: a.label, name: a.name, addressLine1: a.address_line1, addressLine2: a.address_line2 || '',
      city: a.city, province: a.province || '', postalCode: a.postal_code, country: a.country,
      phone: a.phone || '', isDefault: Boolean(a.is_default),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const url = editing ? `${API_BASE_URL}/api/addresses/${editing.id}` : `${API_BASE_URL}/api/addresses`;
    const method = editing ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(formData),
    });
    if (!response.ok) { alert('Failed to save address.'); return; }
    setDialogOpen(false);
    fetchAddresses();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    await fetch(`${API_BASE_URL}/api/addresses/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchAddresses();
  };

  if (loading) return null;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Saved Addresses</Typography>
        <Button variant="contained" onClick={openCreate}>Add Address</Button>
      </Stack>

      {addresses.length === 0 ? (
        <EmptyState icon={<HomeOutlinedIcon />} title="No saved addresses yet" description="Add one here so checkout is faster next time." actionLabel="Add Address" onAction={openCreate} />
      ) : (
        <Grid container spacing={2}>
          {addresses.map((a) => (
            <Grid item xs={12} sm={6} key={a.id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={700}>{a.label}</Typography>
                      {a.is_default && <Chip label="Default" size="small" color="primary" />}
                    </Stack>
                    <Stack direction="row">
                      <IconButton size="small" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Stack>
                  </Stack>
                  <Typography variant="body2">{a.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''}</Typography>
                  <Typography variant="body2" color="text.secondary">{a.city}{a.province ? `, ${a.province}` : ''} {a.postal_code}</Typography>
                  <Typography variant="body2" color="text.secondary">{a.country}</Typography>
                  {a.phone && <Typography variant="body2" color="text.secondary">{a.phone}</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Address' : 'Add Address'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Label" fullWidth value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })}>
                <MenuItem value="Home">Home</MenuItem>
                <MenuItem value="Work">Work</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Full Name" fullWidth value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address Line 1" fullWidth value={formData.addressLine1} onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address Line 2" fullWidth value={formData.addressLine2} onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField label="City" fullWidth value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Province" fullWidth value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Postal Code" fullWidth value={formData.postalCode} onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Phone" fullWidth value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.name || !formData.addressLine1 || !formData.city || !formData.postalCode}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddressesPage;
